import {
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  AutoFixHigh as AIIcon,
  Close,
  ArrowForward as ArrowIcon,
  Lightbulb as RecommendIcon,
} from "@mui/icons-material";
import { FC, ReactElement, useEffect, useMemo, useState } from "react";
import { format, isValid, parseISO } from "date-fns";
import { useSearchParams } from "react-router-dom";

import { DateRangeFilter } from "@/components";
import TariffAnalysisFilters from "./components/TariffAnalysisFilters";
import AiAnalysisDrawer, { type AiEnrichment } from "./components/AiAnalysisDrawer";
import { useAppSelector } from "@/store";
import {
  useGetTariffAnalysisFindingsQuery,
  useGetTariffAnalysisAuthoritySummaryQuery,
  useGetTariffAnalysisAiBucketsQuery,
  useGetTariffAnalysisAiEnrichmentsQuery,
  useTriggerTariffAnalysisAiEnrichmentMutation,
  useTriggerTariffAnalysisAiEnrichmentBulkMutation,
  useGetHTSCodesForDropdownQuery,
  useStartTariffAnalysisBuildMutation,
} from "@/services";
import { useGetCountriesForDropdownQuery, useGetManufacturersForDropdownQuery } from "@/services/report/report-api";
import { roles } from "@/roles";
import { tableCompactSx, tableContainerSx } from "@/components/table/table-styles";
import Pagination from "@/components/paginations/pagination";
import { getCountryFlag } from "@/constants/country-flags";
import { tokens, labelStyle } from "@/tokens";
import {
  AUTHORITY_BUCKETS,
  FindingSign,
  TariffAnalysisAuthority,
} from "./components/tariffAnalysisAggregation";

type AceDutyBreakdownType = {
  mfnDuty: number;
  mtbDuty: number;
  duty201: number;
  duty232: number;
  duty301: number;
  oth99Duties: number;
};

type MetalSplitLineDetail = {
  entrySummaryLineNumber: number;
  htsNumberFull?: string;
  htsDescription?: string;
  goodsValue: number;
  aceDuty: number;
  correctDuty: number;
  expectedRate?: number;
  expectedBreakdown?: { authority: string; rate: number; ch99Codes?: string[] }[];
  ch99CodeList?: string[];
  ch99Exclusions?: Ch99ExclusionAnnotation[];
  aceDutyBreakdown?: AceDutyBreakdownType;
  ch99DutyDict?: Record<string, number>;
  metalType?: string;
};

type TariffAnalysisRow = {
  entrySummaryNumber?: string;
  entrySummaryLineNumber?: number;
  entryDate?: string;
  htsNumberFull?: string;
  htsDescription?: string;
  countryOfOriginCode?: string;
  manufacturerID?: string;
  manufacturerName?: string;
  goodsValue?: number;
  aceDuty: number;
  correctDuty: number;
  difference: number;
  finding: string;
  recommendation?: string;
  ch99?: string;
  ch99Code?: string;
  ch99Code2?: string;
  ch99CodeList?: string[];
  ch99Details?: { code: string; description: string; rate: number }[];
  ch99Exclusions?: Ch99ExclusionAnnotation[];
  expectedRate?: number;
  expectedBreakdown?: { authority: string; rate: number; ch99Codes?: string[] }[];
  aceDutyBreakdown?: AceDutyBreakdownType;
  ch99DutyDict?: Record<string, number>;
  discrepancyAuthorities?: string[];
  authorityBreakdown?: {
    authority: string;
    expectedAmount: number;
    aceAmount: number;
    difference: number;
    codes?: string[];
  }[];
  isMetalSplitBundle?: boolean;
  bundledLineNumbers?: number[];
  metalLines?: MetalSplitLineDetail[];
  nonMetalLines?: MetalSplitLineDetail[];
};

const INITIAL_PAGE = 1;
type TariffAnalysisViewMode = "flat" | "aiAnalysis";

const SIGN_VALUES: FindingSign[] = ["savings", "risk"];
const VIEW_VALUES: TariffAnalysisViewMode[] = ["flat", "aiAnalysis"];

const parseCsvParam = <T extends string>(params: URLSearchParams, key: string, allowed?: readonly T[]): T[] => {
  const raw = params.get(key);
  if (!raw) return [];
  const values = raw.split(",").map((item) => item.trim()).filter(Boolean) as T[];
  return allowed ? values.filter((item) => allowed.includes(item)) : values;
};

const parseBooleanParam = (params: URLSearchParams, key: string) => params.get(key) === "1";

const parseDateParam = (value: string | null): Date | undefined => {
  if (!value) return undefined;
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : undefined;
};

const getDefaultDateRange = () => {
  const endDate = new Date();
  const startDate = new Date(2025, 5, 1); // June 1, 2025
  return { startDate, endDate };
};

const formatHTS = (code?: string) => {
  if (!code) return "-";
  const digits = code.replace(/\D/g, "");
  if (digits.length === 10) {
    return `${digits.slice(0, 4)}.${digits.slice(4, 6)}.${digits.slice(6, 8)}.${digits.slice(8, 10)}`;
  }
  return code;
};

const currency0 = (n?: number) => {
  const value = Number(n || 0);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
};

const formatCh99Dots = (code8: string) =>
  code8.length === 8 ? `${code8.slice(0, 4)}.${code8.slice(4, 6)}.${code8.slice(6, 8)}` : code8;

// Style tokens matching verification-tab / discrepancy-detail pattern
const hdr = { ...labelStyle, whiteSpace: "nowrap" } as const;
const val = { fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.medium, color: tokens.text.body } as const;

const getFindingColor = (finding: string) => {
  const f = finding?.toLowerCase() || "";
  if (f.includes("overpay") || f.includes("saving")) return { bg: tokens.status.successBg, color: tokens.status.success, border: `${tokens.status.success}30` };
  if (f.includes("underpay") || f.includes("risk")) return { bg: tokens.status.dangerBg, color: tokens.status.danger, border: `${tokens.status.danger}40` };
  return { bg: tokens.bg.muted, color: tokens.text.secondary, border: tokens.border.default };
};

type AiBucket = {
  name: string;
  action: string;
  tier: number;
  patternId: string;
  direction: "refund" | "compliance";
  subKey?: string | null;
  absDollars: number;
  pctOfClient: number;
  netDollars: number;
  findingsCount: number;
  findings: TariffAnalysisRow[];
  sampleHts: string[];
  sampleCh99: string[];
  sampleCoo: string[];
};

const STAGE_LABEL: Record<NonNullable<AiEnrichment["currentStage"]>, string> = {
  sampling: "Sampling findings",
  researching: "Researching policy hooks",
  synthesizing: "Synthesizing summary",
  verifying: "Verifying citations",
};

const STALE_HEARTBEAT_MS = 2 * 60 * 1000;

const RunProgress: FC<{ enrichment: AiEnrichment }> = ({ enrichment }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const startedMs = enrichment.startedAt ? new Date(enrichment.startedAt).getTime() : null;
  const heartbeatMs = enrichment.lastHeartbeatAt ? new Date(enrichment.lastHeartbeatAt).getTime() : null;
  const elapsed = startedMs ? Math.floor((now - startedMs) / 1000) : 0;
  const heartbeatAge = heartbeatMs ? now - heartbeatMs : 0;
  const isStale = heartbeatMs != null && heartbeatAge > STALE_HEARTBEAT_MS;
  // Soft cap is 12 by default; we display progress against that.
  const SOFT_CAP = 12;
  const iterations = enrichment.iterations ?? 0;
  const pct = Math.min(100, (iterations / SOFT_CAP) * 100);
  const stageLabel = enrichment.currentStage
    ? STAGE_LABEL[enrichment.currentStage]
    : enrichment.status === "queued"
      ? "Queued"
      : "Starting…";
  const fmt = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };
  return (
    <Box
      sx={{
        p: 1.25,
        mb: 1,
        bgcolor: isStale ? `${tokens.status.warning}10` : `${tokens.brand.primary}08`,
        border: `1px solid ${isStale ? `${tokens.status.warning}40` : `${tokens.brand.primary}25`}`,
        borderRadius: tokens.radius.md,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
        <CircularProgress
          size={12}
          sx={{ color: isStale ? tokens.status.warning : tokens.brand.primary }}
        />
        <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.semibold, color: isStale ? tokens.status.warning : tokens.brand.primary }}>
          {isStale ? "Stalled — no progress in 2 min" : stageLabel}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, fontVariantNumeric: "tabular-nums" }}>
          step {iterations} / {SOFT_CAP}
        </Typography>
        <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, fontVariantNumeric: "tabular-nums" }}>
          {fmt(elapsed)}
        </Typography>
      </Stack>
      <Box
        sx={{
          height: 4,
          width: "100%",
          bgcolor: tokens.bg.muted,
          borderRadius: 2,
          overflow: "hidden",
          mb: enrichment.currentTool ? 0.5 : 0,
        }}
      >
        <Box
          sx={{
            width: `${pct}%`,
            height: "100%",
            bgcolor: isStale ? tokens.status.warning : tokens.brand.primary,
            transition: "width 300ms",
          }}
        />
      </Box>
      {enrichment.currentTool && (
        <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, mt: 0.5 }}>
          last call: <code style={{ fontFamily: "monospace" }}>{enrichment.currentTool}</code>
        </Typography>
      )}
    </Box>
  );
};

const AiBucketsList: FC<{
  buckets: AiBucket[];
  totalDollars: number;
  totalFindings: number;
  refundFindingsCount: number;
  complianceFindingsCount: number;
  onRowClick: (row: TariffAnalysisRow) => void;
  enrichmentByBucketKey?: Map<string, AiEnrichment>;
  onOpenDrawer?: (key: string) => void;
  onRerun?: (key: string) => void;
}> = ({
  buckets,
  totalDollars,
  totalFindings,
  refundFindingsCount,
  complianceFindingsCount,
  onRowClick,
  enrichmentByBucketKey,
  onOpenDrawer,
  onRerun,
}) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [pagination, setPagination] = useState<Record<string, { page: number; limit: number }>>({});

  const DEFAULT_LIMIT = 25;
  const getPagination = (key: string) => pagination[key] ?? { page: 1, limit: DEFAULT_LIMIT };
  const setPage = (key: string, page: number) =>
    setPagination((p) => ({ ...p, [key]: { ...getPagination(key), page } }));
  const setLimit = (key: string, limit: number) =>
    setPagination((p) => ({ ...p, [key]: { page: 1, limit } }));

  if (buckets.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center", color: tokens.text.muted }}>
        No findings in this window — nothing to bucket.
      </Box>
    );
  }

  const totalBucketed = buckets.reduce((s, b) => s + b.findingsCount, 0);
  const unbucketed = totalFindings - totalBucketed;

  const toggle = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Aggregate enrichment job progress across all buckets in this view.
  const enrichmentTallies = (() => {
    let queued = 0;
    let running = 0;
    let done = 0;
    let partial = 0;
    let failed = 0;
    let stale = 0;
    const nowMs = Date.now();
    for (const b of buckets) {
      const k = `${b.patternId}|${b.direction}|${b.subKey ?? ""}`;
      const e = enrichmentByBucketKey?.get(k);
      if (!e) continue;
      if (e.status === "queued") queued += 1;
      else if (e.status === "running") {
        running += 1;
        if (
          e.lastHeartbeatAt &&
          nowMs - new Date(e.lastHeartbeatAt).getTime() > STALE_HEARTBEAT_MS
        ) {
          stale += 1;
        }
      } else if (e.status === "done") done += 1;
      else if (e.status === "partial") partial += 1;
      else if (e.status === "failed") failed += 1;
    }
    return { queued, running, done, partial, failed, stale };
  })();
  const totalEnriched = enrichmentTallies.done + enrichmentTallies.partial;
  const totalActive = enrichmentTallies.queued + enrichmentTallies.running;
  const showAggregate =
    totalEnriched + totalActive + enrichmentTallies.failed > 0;
  const aggregatePct =
    buckets.length > 0
      ? Math.round((totalEnriched / buckets.length) * 100)
      : 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, p: 1 }}>
      <Box sx={{ fontSize: tokens.fontSize.sm, color: tokens.text.muted, mb: 0.5 }}>
        {totalFindings.toLocaleString()} findings (
        <Box component="span" sx={{ color: tokens.status.success, fontWeight: tokens.fontWeight.semibold }}>
          {refundFindingsCount.toLocaleString()} refund
        </Box>
        {" · "}
        <Box component="span" sx={{ color: tokens.status.danger, fontWeight: tokens.fontWeight.semibold }}>
          {complianceFindingsCount.toLocaleString()} compliance
        </Box>
        ) · {currency0(totalDollars)} total |Δ| · {buckets.length} buckets
        {unbucketed > 0 && (
          <Box component="span" sx={{ color: tokens.status.warning, ml: 1 }}>
            · {unbucketed.toLocaleString()} unbucketed
          </Box>
        )}
      </Box>
      {showAggregate && (
        <Box
          sx={{
            p: 1.25,
            mb: 0.5,
            border: `1px solid ${tokens.border.default}`,
            borderRadius: tokens.radius.md,
            bgcolor: tokens.bg.surface,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.75 }}>
            <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: tokens.fontWeight.semibold }}>
              AI Analysis
            </Typography>
            <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, fontVariantNumeric: "tabular-nums" }}>
              {totalEnriched} of {buckets.length} done
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Stack direction="row" spacing={1.5} sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted }}>
              {enrichmentTallies.running > 0 && (
                <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.brand.primary, fontWeight: tokens.fontWeight.semibold }}>
                  {enrichmentTallies.running} running
                </Typography>
              )}
              {enrichmentTallies.queued > 0 && <span>{enrichmentTallies.queued} queued</span>}
              {enrichmentTallies.partial > 0 && (
                <Typography component="span" sx={{ fontSize: tokens.fontSize.xs, color: tokens.status.warning }}>
                  {enrichmentTallies.partial} partial
                </Typography>
              )}
              {enrichmentTallies.failed > 0 && (
                <Typography component="span" sx={{ fontSize: tokens.fontSize.xs, color: tokens.status.danger, fontWeight: tokens.fontWeight.semibold }}>
                  {enrichmentTallies.failed} failed
                </Typography>
              )}
              {enrichmentTallies.stale > 0 && (
                <Typography component="span" sx={{ fontSize: tokens.fontSize.xs, color: tokens.status.warning, fontWeight: tokens.fontWeight.semibold }}>
                  {enrichmentTallies.stale} stalled
                </Typography>
              )}
            </Stack>
          </Stack>
          <Box sx={{ height: 6, width: "100%", bgcolor: tokens.bg.muted, borderRadius: 3, overflow: "hidden" }}>
            <Box
              sx={{
                width: `${aggregatePct}%`,
                height: "100%",
                bgcolor: tokens.brand.primary,
                transition: "width 400ms",
              }}
            />
          </Box>
        </Box>
      )}
      {buckets.map((b, i) => {
        const key = `${b.patternId}|${b.direction}|${b.subKey ?? ""}|${i}`;
        const bucketKey = `${b.patternId}|${b.direction}|${b.subKey ?? ""}`;
        const isOpen = expanded.has(key);
        const refund = b.direction === "refund";
        const directionLabel = refund ? "refund opportunity" : "compliance owe";
        const directionColor = refund ? tokens.status.success : tokens.status.danger;
        const { page: bPage, limit: bLimit } = getPagination(key);
        const pageStart = (bPage - 1) * bLimit;
        const pageFindings = b.findings.slice(pageStart, pageStart + bLimit);
        const enrichment = enrichmentByBucketKey?.get(bucketKey);
        const displayTitle =
          enrichment?.status === "done" || enrichment?.status === "partial"
            ? enrichment.title || b.name
            : b.name;
        return (
          <Box
            key={key}
            sx={{
              border: `1px solid ${tokens.border.default}`,
              borderRadius: tokens.radius.md,
              bgcolor: tokens.bg.surface,
              overflow: "hidden",
            }}
          >
            <Box
              onClick={() => toggle(key)}
              sx={{
                p: 2,
                cursor: "pointer",
                "&:hover": { bgcolor: tokens.bg.hover },
              }}
            >
              <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: tokens.fontSize.sm, color: tokens.text.muted, fontFamily: "monospace" }}>
                    {isOpen ? "▾" : "▸"}
                  </Typography>
                  <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: tokens.fontWeight.semibold,
                        fontSize: tokens.fontSize.sm2,
                        color: tokens.text.primary,
                      }}
                    >
                      {b.tier === 2 ? "↳ " : ""}
                      {displayTitle}
                    </Typography>
                    {enrichment && (
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                        {enrichment.verdict === "systemError" && (
                          <Chip
                            label="Likely false positive"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: tokens.fontSize["2xs"],
                              fontWeight: tokens.fontWeight.bold,
                              bgcolor: `${tokens.status.danger}15`,
                              color: tokens.status.danger,
                              border: `1px solid ${tokens.status.danger}40`,
                            }}
                          />
                        )}
                        {enrichment.verdict === "ambiguous" && (
                          <Chip
                            label="Ambiguous"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: tokens.fontSize["2xs"],
                              fontWeight: tokens.fontWeight.bold,
                              bgcolor: `${tokens.status.warning}15`,
                              color: tokens.status.warning,
                              border: `1px solid ${tokens.status.warning}40`,
                            }}
                          />
                        )}
                        {enrichment.verdict === "confirmed" && (
                          <Chip
                            label="Confirmed"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: tokens.fontSize["2xs"],
                              fontWeight: tokens.fontWeight.bold,
                              bgcolor: `${tokens.status.success}15`,
                              color: tokens.status.success,
                              border: `1px solid ${tokens.status.success}40`,
                            }}
                          />
                        )}
                        {enrichment.status === "queued" && (
                          <Chip label="Queued" size="small" sx={{ height: 18, fontSize: tokens.fontSize["2xs"] }} />
                        )}
                        {enrichment.status === "running" && (
                          <Chip
                            label="Running"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: tokens.fontSize["2xs"],
                              bgcolor: `${tokens.brand.primary}15`,
                              color: tokens.brand.primary,
                            }}
                          />
                        )}
                        {enrichment.status === "failed" && (
                          <Chip
                            label="Re-run failed"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: tokens.fontSize["2xs"],
                              bgcolor: `${tokens.status.danger}15`,
                              color: tokens.status.danger,
                            }}
                          />
                        )}
                        {enrichment.status === "partial" && (
                          <Chip
                            label="Partial result"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: tokens.fontSize["2xs"],
                              bgcolor: `${tokens.status.warning}15`,
                              color: tokens.status.warning,
                            }}
                          />
                        )}
                      </Stack>
                    )}
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ flexShrink: 0 }}>
                  <Typography sx={{ fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.sm2, color: tokens.text.primary }}>
                    {currency0(b.absDollars)}
                  </Typography>
                  <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted }}>
                    {b.pctOfClient.toFixed(1)}%
                  </Typography>
                  <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted }}>
                    {b.findingsCount.toLocaleString()} findings
                  </Typography>
                  {(() => {
                    const isRunning =
                      enrichment?.status === "queued" || enrichment?.status === "running";
                    const hasResult =
                      enrichment?.status === "done" || enrichment?.status === "partial";
                    const failed = enrichment?.status === "failed";
                    const label = isRunning
                      ? "Running…"
                      : failed
                      ? "Retry AI"
                      : hasResult
                      ? "Re-run AI"
                      : "Run AI";
                    return (
                      <Chip
                        label={label}
                        size="small"
                        clickable={!isRunning}
                        disabled={isRunning}
                        icon={
                          isRunning ? (
                            <CircularProgress size={10} sx={{ color: tokens.brand.primary }} />
                          ) : undefined
                        }
                        onClick={(e: any) => {
                          e.stopPropagation();
                          if (isRunning) return;
                          onRerun?.(bucketKey);
                        }}
                        sx={{
                          height: 22,
                          fontSize: tokens.fontSize["2xs"],
                          fontWeight: tokens.fontWeight.bold,
                          bgcolor: isRunning
                            ? `${tokens.brand.primary}15`
                            : failed
                            ? `${tokens.status.danger}10`
                            : tokens.brand.primary,
                          color: isRunning
                            ? tokens.brand.primary
                            : failed
                            ? tokens.status.danger
                            : "#FFFFFF",
                          border: failed ? `1px solid ${tokens.status.danger}40` : "none",
                          "&:hover": {
                            bgcolor: isRunning
                              ? `${tokens.brand.primary}15`
                              : failed
                              ? `${tokens.status.danger}20`
                              : tokens.brand.primaryHover,
                          },
                        }}
                      />
                    );
                  })()}
                  {(enrichment?.status === "done" || enrichment?.status === "partial") && (
                    <Chip
                      label="View AI →"
                      size="small"
                      clickable
                      onClick={(e: any) => {
                        e.stopPropagation();
                        onOpenDrawer?.(bucketKey);
                      }}
                      sx={{
                        height: 22,
                        fontSize: tokens.fontSize["2xs"],
                        fontWeight: tokens.fontWeight.bold,
                        bgcolor: tokens.bg.surface,
                        color: tokens.brand.primary,
                        border: `1px solid ${tokens.brand.primary}40`,
                        "&:hover": {
                          bgcolor: `${tokens.brand.primary}10`,
                          borderColor: tokens.brand.primary,
                        },
                      }}
                    />
                  )}
                  <Chip
                    label={directionLabel}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: tokens.fontSize["2xs"],
                      fontWeight: tokens.fontWeight.bold,
                      bgcolor: `${directionColor}15`,
                      color: directionColor,
                      border: `1px solid ${directionColor}30`,
                    }}
                  />
                </Stack>
              </Stack>
              {enrichment?.verdict === "systemError" && enrichment.verdictReason && (
                <Box
                  sx={{
                    p: 1.25,
                    mb: 1,
                    bgcolor: `${tokens.status.danger}10`,
                    border: `1px solid ${tokens.status.danger}40`,
                    borderRadius: tokens.radius.md,
                  }}
                >
                  <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.status.danger, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: tokens.fontWeight.bold, mb: 0.25 }}>
                    Calc-engine review recommended
                  </Typography>
                  <Typography sx={{ fontSize: tokens.fontSize.sm, color: tokens.text.body }}>
                    {enrichment.verdictReason}
                  </Typography>
                </Box>
              )}
              {enrichment?.verdict === "ambiguous" && enrichment.verdictReason && (
                <Box
                  sx={{
                    p: 1.25,
                    mb: 1,
                    bgcolor: `${tokens.status.warning}10`,
                    border: `1px solid ${tokens.status.warning}40`,
                    borderRadius: tokens.radius.md,
                  }}
                >
                  <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.status.warning, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: tokens.fontWeight.bold, mb: 0.25 }}>
                    Need additional documentation
                  </Typography>
                  <Typography sx={{ fontSize: tokens.fontSize.sm, color: tokens.text.body }}>
                    {enrichment.verdictReason}
                  </Typography>
                </Box>
              )}
              <Typography
                sx={{
                  fontSize: tokens.fontSize.sm,
                  color: enrichment?.verdict === "systemError" ? tokens.text.muted : tokens.text.body,
                  textDecoration: enrichment?.verdict === "systemError" ? "line-through" : undefined,
                  mb: 1,
                }}
              >
                {b.action}
              </Typography>
              {enrichment && (enrichment.status === "running" || enrichment.status === "queued") && (
                <RunProgress enrichment={enrichment} />
              )}
              {enrichment?.status === "failed" && enrichment.error && (
                <Box
                  sx={{
                    p: 1,
                    mb: 1,
                    bgcolor: `${tokens.status.danger}10`,
                    border: `1px solid ${tokens.status.danger}30`,
                    borderRadius: tokens.radius.md,
                    fontSize: tokens.fontSize.xs,
                    color: tokens.status.danger,
                    fontFamily: "monospace",
                  }}
                >
                  {enrichment.error}
                </Box>
              )}
              <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
                {b.sampleHts.length > 0 && (
                  <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, fontFamily: "monospace" }}>
                    HTS: {b.sampleHts.slice(0, 4).join(", ")}
                  </Typography>
                )}
                {b.sampleCh99.length > 0 && (
                  <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, fontFamily: "monospace" }}>
                    Ch99: {b.sampleCh99.slice(0, 4).join(", ")}
                  </Typography>
                )}
                {b.sampleCoo.length > 0 && (
                  <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted }}>
                    COO: {b.sampleCoo.slice(0, 4).join(", ")}
                  </Typography>
                )}
              </Stack>
            </Box>
            {isOpen && (
              <Box sx={{ borderTop: `1px solid ${tokens.border.default}`, bgcolor: tokens.bg.muted }}>
                {enrichment && (enrichment.status === "done" || enrichment.status === "partial") && (
                  <Box sx={{ p: 2, borderBottom: `1px solid ${tokens.border.default}`, bgcolor: tokens.bg.surface }}>
                    {enrichment.summary && (
                      <Typography sx={{ fontSize: tokens.fontSize.sm, mb: 1, lineHeight: 1.55 }}>
                        {enrichment.summary.split(/\n\n+/)[0]}
                      </Typography>
                    )}
                    {enrichment.recommendedAction && (
                      <Typography sx={{ fontSize: tokens.fontSize.sm, fontWeight: tokens.fontWeight.semibold, mb: 1 }}>
                        → {enrichment.recommendedAction}
                      </Typography>
                    )}
                    {(enrichment.citations || []).slice(0, 3).map((c, ci) => (
                      <Chip
                        key={ci}
                        label={`${c.sourceType} · ${c.identifier}`}
                        size="small"
                        component="a"
                        href={c.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        clickable
                        sx={{ mr: 0.5, mb: 0.5, height: 18, fontSize: tokens.fontSize["2xs"] }}
                      />
                    ))}
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Chip
                        label="View AI analysis →"
                        size="small"
                        clickable
                        onClick={(e: any) => {
                          e.stopPropagation();
                          onOpenDrawer?.(bucketKey);
                        }}
                        sx={{
                          height: 22,
                          fontSize: tokens.fontSize.xs,
                          fontWeight: tokens.fontWeight.semibold,
                          bgcolor: tokens.brand.primary,
                          color: "#FFF",
                        }}
                      />
                      <Chip
                        label="Re-run agent"
                        size="small"
                        clickable
                        onClick={(e: any) => {
                          e.stopPropagation();
                          onRerun?.(bucketKey);
                        }}
                        sx={{ height: 22, fontSize: tokens.fontSize.xs }}
                      />
                    </Stack>
                  </Box>
                )}
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell align="left">Entry / Product</TableCell>
                      <TableCell align="center">Origin</TableCell>
                      <TableCell align="right">ACE Duty</TableCell>
                      <TableCell align="right">Correct Duty</TableCell>
                      <TableCell align="right">Difference</TableCell>
                      <TableCell align="left">Finding</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pageFindings.map((item, idx) => {
                      const isBundle = !!item.isMetalSplitBundle;
                      const entryLine = isBundle && item.bundledLineNumbers?.length
                        ? `${item.entrySummaryNumber || "-"}-${item.bundledLineNumbers.join(", ")}`
                        : `${item.entrySummaryNumber || "-"}-${item.entrySummaryLineNumber ?? "-"}`;
                      const hts = formatHTS(item.htsNumberFull);
                      const desc = item.htsDescription ? ` - ${item.htsDescription}` : "";
                      const diff = Number(item.difference || 0);
                      const sign = diff > 0 ? "+" : diff < 0 ? "-" : "";
                      const diffColor = diff > 0 ? tokens.status.success : diff < 0 ? tokens.status.danger : tokens.text.primary;
                      const origin = item.countryOfOriginCode || "-";
                      const flag = origin !== "-" ? getCountryFlag(origin) : "";

                      return (
                        <TableRow
                          key={`${item.entrySummaryNumber}-${item.entrySummaryLineNumber}-${idx}`}
                          onClick={() => onRowClick(item)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell align="left">
                            <Stack spacing={0.25}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography sx={{ fontWeight: tokens.fontWeight.bold, fontFamily: "monospace", fontSize: tokens.fontSize.base }}>{entryLine}</Typography>
                                {isBundle && (
                                  <Chip label="Metal Split" size="small" sx={{ height: 18, fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.bold, bgcolor: "#EDE9FE", color: "#6D28D9", borderRadius: 0.5 }} />
                                )}
                              </Stack>
                              {isBundle ? (
                                <>
                                  {item.metalLines?.map((ml) => (
                                    <Typography key={`m-${ml.entrySummaryLineNumber}`} noWrap sx={{ color: tokens.text.secondary, fontSize: tokens.fontSize.xs, maxWidth: 280 }}>
                                      {formatHTS(ml.htsNumberFull)} ({ml.metalType === 'STEEL' ? 'Steel' : ml.metalType === 'ALUMINUM' ? 'Aluminum' : ml.metalType === 'COPPER' ? 'Copper' : 'Metal'})
                                    </Typography>
                                  ))}
                                  {item.nonMetalLines?.map((nml) => (
                                    <Typography key={`nm-${nml.entrySummaryLineNumber}`} noWrap sx={{ color: tokens.text.secondary, fontSize: tokens.fontSize.xs, maxWidth: 280 }}>
                                      {formatHTS(nml.htsNumberFull)} (Non-Metal)
                                    </Typography>
                                  ))}
                                </>
                              ) : (
                                <Typography noWrap sx={{ color: tokens.text.secondary, fontSize: tokens.fontSize.xs, maxWidth: 280 }}>
                                  {hts}{desc}
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                              {flag && <span style={{ fontSize: 14 }}>{flag}</span>}
                              <Typography sx={{ whiteSpace: "nowrap", fontWeight: tokens.fontWeight.semibold }}>{origin}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ whiteSpace: "nowrap", fontWeight: tokens.fontWeight.semibold }}>{currency0(item.aceDuty)}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ whiteSpace: "nowrap", fontWeight: tokens.fontWeight.semibold }}>{currency0(item.correctDuty)}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ whiteSpace: "nowrap", fontWeight: tokens.fontWeight.bold, color: diffColor }}>
                              {sign}{currency0(Math.abs(diff))}
                            </Typography>
                          </TableCell>
                          <TableCell align="left">
                            <Tooltip title={item.finding || "-"} arrow placement="top-start">
                              <Typography noWrap sx={{ maxWidth: 200, fontWeight: tokens.fontWeight.semibold }}>{item.finding || "-"}</Typography>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {b.findingsCount > DEFAULT_LIMIT && (
                  <Pagination
                    page={bPage}
                    limit={bLimit}
                    total={b.findingsCount}
                    onPageChange={(p) => setPage(key, p)}
                    onLimitChange={(l) => setLimit(key, l)}
                  />
                )}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const TariffAnalysisView: FC = (): ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAppSelector((s) => s.auth);
  const { selectedClient } = useAppSelector((s) => (s as any).client);
  const { selectedTenant } = useAppSelector((s) => (s as any).tenantManagement);

  const isAdmin = user?.userRole === roles.TDADMIN;
  const canSelectClients = useMemo(() => {
    const allowedRoles = [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  const shouldSkip = useMemo(() => {
    if (user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT") {
      return false;
    }
    return canSelectClients && !selectedClient;
  }, [canSelectClients, selectedClient, selectedTenant, user]);

  const clientIdForDropdowns = useMemo(() => {
    if (selectedClient?._id) return selectedClient._id;
    if (user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT") {
      return selectedTenant?._id;
    }
    return undefined;
  }, [selectedClient?._id, user?.userRole, selectedTenant?._id, selectedTenant?.tenantType]);

  const [page, setPage] = useState(() => Number(searchParams.get("page") || INITIAL_PAGE));
  const [limit, setLimit] = useState(() => Number(searchParams.get("limit") || 20));
  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [dateFilter, setDateFilter] = useState<{ startDate?: Date; endDate?: Date }>(() => {
    const fallback = getDefaultDateRange();
    return {
      startDate: parseDateParam(searchParams.get("dateFrom")) || fallback.startDate,
      endDate: parseDateParam(searchParams.get("dateTo")) || fallback.endDate,
    };
  });

  const [manufacturerIds, setManufacturerIds] = useState<string[]>(() => parseCsvParam(searchParams, "manufacturer"));
  const [originCodes, setOriginCodes] = useState<string[]>(() => parseCsvParam(searchParams, "coo"));
  const [htsCodes, setHtsCodes] = useState<string[]>(() => parseCsvParam(searchParams, "hts"));
  const [signs, setSigns] = useState<FindingSign[]>(() => parseCsvParam(searchParams, "sign", SIGN_VALUES));
  const [authorityFilters, setAuthorityFilters] = useState<TariffAnalysisAuthority[]>(() =>
    parseCsvParam(searchParams, "authority", AUTHORITY_BUCKETS)
  );
  const [hasCh99, setHasCh99] = useState(() => parseBooleanParam(searchParams, "hasCh99"));
  const [metalSplitOnly, setMetalSplitOnly] = useState(() => parseBooleanParam(searchParams, "metalSplit"));
  const [minAbsDifference, setMinAbsDifference] = useState(() => searchParams.get("minAbs") || "");
  const [maxAbsDifference, setMaxAbsDifference] = useState(() => searchParams.get("maxAbs") || "");
  const [viewMode, setViewMode] = useState<TariffAnalysisViewMode>(() => {
    const raw = searchParams.get("view") as TariffAnalysisViewMode | null;
    return raw && VIEW_VALUES.includes(raw) ? raw : "flat";
  });

  const [selectedRow, setSelectedRow] = useState<TariffAnalysisRow | null>(null);

  useEffect(() => {
    const fallback = getDefaultDateRange();
    const nextView = searchParams.get("view") as TariffAnalysisViewMode | null;

    setViewMode(nextView && VIEW_VALUES.includes(nextView) ? nextView : "flat");
    setPage(Number(searchParams.get("page") || INITIAL_PAGE));
    setLimit(Number(searchParams.get("limit") || 20));
    setSearch(searchParams.get("search") || "");
    setDateFilter((prev) => {
      const startDate = parseDateParam(searchParams.get("dateFrom")) || fallback.startDate;
      const endDate = parseDateParam(searchParams.get("dateTo")) || fallback.endDate;
      if (
        prev.startDate?.getTime() === startDate?.getTime() &&
        prev.endDate?.getTime() === endDate?.getTime()
      ) {
        return prev;
      }
      return { startDate, endDate };
    });
    setManufacturerIds(parseCsvParam(searchParams, "manufacturer"));
    setOriginCodes(parseCsvParam(searchParams, "coo"));
    setHtsCodes(parseCsvParam(searchParams, "hts"));
    setSigns(parseCsvParam(searchParams, "sign", SIGN_VALUES));
    setAuthorityFilters(parseCsvParam(searchParams, "authority", AUTHORITY_BUCKETS));
    setMinAbsDifference(searchParams.get("minAbs") || "");
    setMaxAbsDifference(searchParams.get("maxAbs") || "");
    setHasCh99(parseBooleanParam(searchParams, "hasCh99"));
    setMetalSplitOnly(parseBooleanParam(searchParams, "metalSplit"));
  }, [searchParams]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (viewMode !== "flat") next.set("view", viewMode);
    if (page !== INITIAL_PAGE) next.set("page", String(page));
    if (limit !== 20) next.set("limit", String(limit));
    if (search.trim()) next.set("search", search.trim());
    if (dateFilter.startDate) next.set("dateFrom", format(dateFilter.startDate, "yyyy-MM-dd"));
    if (dateFilter.endDate) next.set("dateTo", format(dateFilter.endDate, "yyyy-MM-dd"));
    if (manufacturerIds.length) next.set("manufacturer", manufacturerIds.join(","));
    if (originCodes.length) next.set("coo", originCodes.join(","));
    if (htsCodes.length) next.set("hts", htsCodes.join(","));
    if (signs.length) next.set("sign", signs.join(","));
    if (authorityFilters.length) next.set("authority", authorityFilters.join(","));
    if (minAbsDifference) next.set("minAbs", minAbsDifference);
    if (maxAbsDifference) next.set("maxAbs", maxAbsDifference);
    if (hasCh99) next.set("hasCh99", "1");
    if (metalSplitOnly) next.set("metalSplit", "1");

    if (next.toString() !== window.location.search.slice(1)) {
      setSearchParams(next, { replace: true });
    }
  }, [
    authorityFilters,
    dateFilter,
    hasCh99,
    htsCodes,
    limit,
    manufacturerIds,
    maxAbsDifference,
    metalSplitOnly,
    minAbsDifference,
    originCodes,
    page,
    search,
    setSearchParams,
    signs,
    viewMode,
  ]);

  const filters = useMemo(() => {
    const f: Record<string, any> = {};
    if (manufacturerIds.length) f.manufacturerID = { value: manufacturerIds, operator: "in" };
    if (originCodes.length) f.countryOfOriginCode = { value: originCodes, operator: "in" };
    if (htsCodes.length) f.htsNumberFull = { value: htsCodes, operator: "in" };
    return f;
  }, [manufacturerIds, originCodes, htsCodes]);

  const isAiView = viewMode === "aiAnalysis";

  const queryInput = useMemo(() => {
    const minValue = minAbsDifference.trim() ? Number(minAbsDifference) : undefined;
    const maxValue = maxAbsDifference.trim() ? Number(maxAbsDifference) : undefined;
    return {
      input: {
        pageNo: page,
        pageSize: limit,
        searchKeyword: search.trim() || undefined,
        filters: Object.keys(filters).length ? filters : undefined,
        ...(signs.length > 0 && { signs }),
        ...(authorityFilters.length > 0 && { authorities: authorityFilters }),
        ...(hasCh99 && { hasCh99 }),
        ...(metalSplitOnly && { metalSplitOnly }),
        ...(Number.isFinite(minValue) && { minAbsDifference: minValue }),
        ...(Number.isFinite(maxValue) && { maxAbsDifference: maxValue }),
        ...(dateFilter.startDate && { dateFrom: format(dateFilter.startDate, "yyyy-MM-dd") }),
        ...(dateFilter.endDate && { dateTo: format(dateFilter.endDate, "yyyy-MM-dd") }),
        ...(user?.userRole &&
          [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN].includes(user.userRole) && {
            clientId: clientIdForDropdowns,
          }),
        ...(user?.userRole && [roles.TDADMIN].includes(user.userRole) && { tenantId: selectedTenant?._id }),
      },
    };
  }, [
    authorityFilters,
    clientIdForDropdowns,
    dateFilter,
    filters,
    hasCh99,
    limit,
    maxAbsDifference,
    metalSplitOnly,
    minAbsDifference,
    page,
    search,
    selectedTenant,
    signs,
    user,
  ]);

  const { data, isLoading, isFetching, refetch } = useGetTariffAnalysisFindingsQuery(queryInput, {
    skip: shouldSkip,
    refetchOnMountOrArgChange: true,
  } as any);

  const aggregateQueryInput = useMemo(
    () => ({
      input: {
        ...queryInput.input,
        pageNo: undefined,
        pageSize: undefined,
      },
    }),
    [queryInput]
  );

  // Authority summary always fires (when a client is selected) so the filter
  // panel can show $ amounts inline next to each authority option. Cheap.
  const {
    data: authoritySummaryData,
    refetch: refetchAuthoritySummary,
  } = useGetTariffAnalysisAuthoritySummaryQuery(aggregateQueryInput, {
    skip: shouldSkip,
    refetchOnMountOrArgChange: true,
  } as any);

  const {
    data: aiBucketsData,
    isLoading: isAiBucketsLoading,
    isFetching: isAiBucketsFetching,
    refetch: refetchAiBuckets,
  } = useGetTariffAnalysisAiBucketsQuery(aggregateQueryInput, {
    skip: shouldSkip || !isAiView,
    refetchOnMountOrArgChange: true,
  } as any);

  const aiBuckets: AiBucket[] = (aiBucketsData as any)?.tariffAnalysisAiBuckets?.data?.buckets ?? [];
  const aiClientTotal: number = (aiBucketsData as any)?.tariffAnalysisAiBuckets?.data?.absDollars ?? 0;
  const aiFindingsCount: number = (aiBucketsData as any)?.tariffAnalysisAiBuckets?.data?.findingsCount ?? 0;
  const aiRefundCount: number = (aiBucketsData as any)?.tariffAnalysisAiBuckets?.data?.refundFindingsCount ?? 0;
  const aiComplianceCount: number = (aiBucketsData as any)?.tariffAnalysisAiBuckets?.data?.complianceFindingsCount ?? 0;

  const { data: aiEnrichmentsData } = useGetTariffAnalysisAiEnrichmentsQuery(aggregateQueryInput, {
    skip: shouldSkip || !isAiView,
    refetchOnMountOrArgChange: true,
    pollingInterval: isAiView ? 5000 : 0,
  } as any);
  const enrichments: AiEnrichment[] = (aiEnrichmentsData as any)?.tariffAnalysisAiEnrichments?.data ?? [];
  const enrichmentByBucketKey = useMemo(() => {
    const m = new Map<string, AiEnrichment>();
    for (const e of enrichments) m.set(e.bucketKey, e);
    return m;
  }, [enrichments]);
  const [drawerBucketKey, setDrawerBucketKey] = useState<string | null>(null);
  const [triggerEnrichment] = useTriggerTariffAnalysisAiEnrichmentMutation();
  const [triggerEnrichmentBulk, { isLoading: isTriggeringBulk }] =
    useTriggerTariffAnalysisAiEnrichmentBulkMutation();
  const handleRunAiAnalysis = async () => {
    const cid = (queryInput.input as any).clientId;
    const tid = (queryInput.input as any).tenantId;
    if (!cid) return;
    try {
      // force: true so user-initiated bulk runs bypass the 24h cap.
      await triggerEnrichmentBulk({
        input: { clientId: cid, tenantId: tid, force: true },
      }).unwrap();
    } catch (e) {
      console.error("Failed to trigger AI analysis:", e);
    }
  };
  const anyEnrichmentRunning = useMemo(
    () =>
      enrichments.some(
        (e) => e.status === "queued" || e.status === "running"
      ),
    [enrichments]
  );

  const rows: TariffAnalysisRow[] = (data as any)?.tariffAnalysisFindings?.data ?? [];
  const totalCount: number = (data as any)?.tariffAnalysisFindings?.meta?.countDocs ?? 0;
  const buildStatus =
    (data as any)?.tariffAnalysisFindings?.build ||
    (aiBucketsData as any)?.tariffAnalysisAiBuckets?.build;
  const isPageLoading = isAiView ? isAiBucketsLoading : isLoading;
  const isPageFetching = isAiView ? isAiBucketsFetching : isFetching;

  // Map authority → { count, dollars } for the filter panel.
  const authoritySummaries: Array<{ authority: string; count: number; savings: number; risk: number; net: number }> =
    (authoritySummaryData as any)?.tariffAnalysisAuthoritySummary?.data ?? [];
  const authorityAmounts = useMemo(() => {
    const m = new Map<string, { count: number; dollars: number }>();
    for (const r of authoritySummaries) {
      m.set(r.authority, { count: r.count, dollars: (r.savings || 0) + (r.risk || 0) });
    }
    return m;
  }, [authoritySummaries]);

  // Rebuild mutation
  const [startBuild, { isLoading: isStartingBuild }] = useStartTariffAnalysisBuildMutation();

  const handleRebuild = async () => {
    try {
      await startBuild({ input: queryInput.input });
      const cid = (queryInput.input as any).clientId;
      const tid = (queryInput.input as any).tenantId;
      if (cid) {
        triggerEnrichmentBulk({ input: { clientId: cid, tenantId: tid } }).catch(() => undefined);
      }
      refetchAuthoritySummary();
      if (isAiView) refetchAiBuckets();
      else refetch();
    } catch (error) {
      console.error("Failed to start tariff analysis rebuild:", error);
    }
  };

  const isBuildInProgress = buildStatus?.status === "inprocess";

  // Poll for build progress updates while a build is in progress
  useEffect(() => {
    if (!isBuildInProgress) return;

    const interval = setInterval(() => {
      refetchAuthoritySummary();
      if (isAiView) refetchAiBuckets();
      else refetch();
    }, 2000);

    return () => clearInterval(interval);
  }, [isAiView, isBuildInProgress, refetch, refetchAuthoritySummary, refetchAiBuckets]);

  const { data: htsOptions = [] } = useGetHTSCodesForDropdownQuery(
    {
      clientId: clientIdForDropdowns,
      ...(isAdmin && { tenantId: selectedTenant?._id }),
      includeChapter99: false,
    },
    { skip: shouldSkip }
  );

  const { data: countryOptions = [] } = useGetCountriesForDropdownQuery(
    {
      clientId: clientIdForDropdowns || "",
      dateFrom: dateFilter.startDate ? format(dateFilter.startDate, "yyyy-MM-dd") : undefined,
      dateTo: dateFilter.endDate ? format(dateFilter.endDate, "yyyy-MM-dd") : undefined,
    },
    { skip: shouldSkip }
  );

  const { data: manufacturerOptions = [] } = useGetManufacturersForDropdownQuery(
    {
      dashboardVisibility: {
        clientId: clientIdForDropdowns,
        ...(isAdmin && { tenantId: selectedTenant?._id }),
      },
    },
    { skip: shouldSkip }
  );

  const htsOnlyOptions = useMemo(() => {
    return (htsOptions as any[]).filter((x) => x.type === "hts");
  }, [htsOptions]);

  const handleClear = () => {
    setManufacturerIds([]);
    setOriginCodes([]);
    setHtsCodes([]);
    setSigns([]);
    setAuthorityFilters([]);
    setHasCh99(false);
    setMetalSplitOnly(false);
    setMinAbsDifference("");
    setMaxAbsDifference("");
    setViewMode("flat");
    setSearch("");
    setDateFilter(getDefaultDateRange());
    setPage(INITIAL_PAGE);
  };

  if (shouldSkip && canSelectClients) {
    return (
      <Stack sx={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <Typography sx={{ fontSize: 16, fontWeight: tokens.fontWeight.semibold, color: tokens.text.secondary }}>
          Please select a client to view tariff analysis
        </Typography>
      </Stack>
    );
  }

  // Side-by-side duty breakdown — see docs/FINDING_DETAIL_DRAWER.md for the spec.
  // Each row pairs an expected authority with what was filed in ACE for that
  // same authority/code, with a status pill in the center gutter. Every dollar
  // in r.aceDuty is accounted for: bucket totals, per-code dict attribution,
  // ACE-only extras, and a residual catch-all for unattributed Ch99 charges.
  const renderDutyBreakdownTable = ({
    expectedBreakdown,
    ace,
    goodsValue,
    aceDuty,
    dutyDict,
    ch99Details,
    correctDuty,
  }: {
    expectedBreakdown: { authority: string; rate: number; ch99Codes?: string[] }[];
    ace: AceDutyBreakdownType | null | undefined;
    goodsValue: number;
    aceDuty: number;
    expectedRate?: number;
    lineCh99Codes?: string[];
    dutyDict?: Record<string, number> | null;
    ch99Details?: { code: string; description: string; rate: number }[] | null;
    correctDuty?: number;
  }) => {
    const gv = goodsValue || 0;
    const showHeader = !!ace || (expectedBreakdown && expectedBreakdown.length > 0);
    if (!showHeader) return null;

    type Side = { label: string; code?: string; description?: string; rate: number; amount: number };
    type Pair = { ace: Side | null; expected: Side | null; status: "match" | "over" | "under" | "missing" | "extra" };

    const detailMap = new Map<string, { code: string; description: string; rate: number }>();
    for (const d of ch99Details || []) {
      const k = String(d.code || "").replace(/\D/g, "");
      if (k) detailMap.set(k, d);
    }

    const mapToAce = (auth: string): keyof AceDutyBreakdownType => {
      const l = auth.toLowerCase();
      if (l.includes("mfn") || l.includes("general duty")) return "mfnDuty";
      if (l.includes("232")) return "duty232";
      if (l.includes("301")) return "duty301";
      if (l.includes("201")) return "duty201";
      if (l.includes("mtb")) return "mtbDuty";
      return "oth99Duties";
    };

    const extractCh99FromAuth = (auth: string): string | null => {
      const m = auth.match(/9903[.\s]?\d{2}[.\s]?\d{2}/);
      if (!m) return null;
      return m[0].replace(/\D/g, "").padEnd(8, "0").slice(0, 8);
    };

    const ch99PrefixToBucket = (code: string): keyof AceDutyBreakdownType => {
      const c = String(code).replace(/\D/g, "").padEnd(8, "0");
      if (c.startsWith("990388")) return "duty301";
      if (c.startsWith("990380") || c.startsWith("990381") || c.startsWith("990385")) return "duty232";
      if (c.startsWith("990382") || c.startsWith("990392") || c.startsWith("990390") || c.startsWith("990360")) return "duty232";
      if (c.startsWith("990372") || c.startsWith("990373")) return "duty201";
      return "oth99Duties";
    };

    const bucketLabel: Record<Exclude<keyof AceDutyBreakdownType, "oth99Duties">, string> = {
      mfnDuty: "MFN Duty",
      mtbDuty: "MTB Duty",
      duty201: "Section 201",
      duty232: "Section 232",
      duty301: "Section 301",
    };

    const sorted = (expectedBreakdown || []).slice().sort((a, b) => (b.rate ?? 0) - (a.rate ?? 0));
    const consumedBuckets = new Set<string>();
    const consumedDictCodes = new Set<string>();
    // Tracks dollars in ace.oth99Duties already attributed to a specific
    // Ch99 code so the dict-extras loop doesn't double-count them.
    let oth99PairedAceTotal = 0;
    const pairs: Pair[] = [];

    for (const rule of sorted) {
      const rate = rule.rate ?? 0;
      const expectedAmount = rate * gv;
      const cat = mapToAce(rule.authority);
      const expCode = extractCh99FromAuth(rule.authority);
      const expDetail = expCode ? detailMap.get(expCode) : undefined;
      const expected: Side = {
        label: rule.authority,
        code: expCode || undefined,
        description: expDetail?.description,
        rate,
        amount: expectedAmount,
      };

      let aceSide: Side | null = null;
      if (ace) {
        if (cat !== "oth99Duties") {
          const amt = (ace[cat] as number) || 0;
          consumedBuckets.add(cat);
          aceSide = { label: bucketLabel[cat], rate: gv > 0 ? amt / gv : 0, amount: amt };
        } else if (expCode && dutyDict && dutyDict[expCode] != null) {
          const amt = dutyDict[expCode];
          consumedDictCodes.add(expCode);
          oth99PairedAceTotal += amt;
          const d = detailMap.get(expCode);
          aceSide = {
            label: `Ch99 ${formatCh99Dots(expCode)}`,
            code: expCode,
            description: d?.description,
            rate: gv > 0 ? amt / gv : 0,
            amount: amt,
          };
        } else {
          // Try to pair the expected with a single unconsumed oth99 dict code
          // — the broker often filed the WRONG Ch99, and naming it makes the
          // mismatch obvious. With multiple candidates, leave aceSide null;
          // the dict-extras loop below renders each filed code as its own row.
          const oth99Candidates = dutyDict
            ? Object.keys(dutyDict).filter((c) => {
                if (consumedDictCodes.has(c)) return false;
                const amt = dutyDict[c];
                return amt != null && amt > 0 && ch99PrefixToBucket(c) === "oth99Duties";
              })
            : [];
          if (oth99Candidates.length === 1 && dutyDict) {
            const code = oth99Candidates[0];
            const amt = dutyDict[code] || 0;
            consumedDictCodes.add(code);
            oth99PairedAceTotal += amt;
            const d = detailMap.get(code);
            aceSide = {
              label: `Ch99 ${formatCh99Dots(code)}`,
              code,
              description: d?.description,
              rate: gv > 0 ? amt / gv : 0,
              amount: amt,
            };
          }
        }
      }

      let status: Pair["status"];
      if (!aceSide || aceSide.amount === 0) status = "missing";
      else if (Math.abs(aceSide.amount - expected.amount) <= 0.5) status = "match";
      else if (aceSide.amount > expected.amount) status = "over";
      else status = "under";

      pairs.push({ ace: aceSide, expected, status });
    }

    if (ace) {
      const others: Exclude<keyof AceDutyBreakdownType, "oth99Duties">[] = ["mfnDuty", "mtbDuty", "duty201", "duty232", "duty301"];
      for (const b of others) {
        if (consumedBuckets.has(b)) continue;
        const amt = (ace[b] as number) || 0;
        if (amt <= 0) continue;
        pairs.push({
          ace: { label: bucketLabel[b], rate: gv > 0 ? amt / gv : 0, amount: amt },
          expected: null,
          status: "extra",
        });
      }
      if (dutyDict) {
        let oth99RemainingForExtras = (ace.oth99Duties || 0) - oth99PairedAceTotal;
        for (const code of Object.keys(dutyDict)) {
          if (consumedDictCodes.has(code)) continue;
          const amt = dutyDict[code];
          if (amt == null || amt <= 0) continue;
          const codeBucket = ch99PrefixToBucket(code);
          if (codeBucket !== "oth99Duties" && consumedBuckets.has(codeBucket)) continue;
          // For oth99-mapped codes: don't double-list. The bucket-fallback or
          // dict-matched branches above may have already counted these dollars.
          let surfaceAmt = amt;
          if (codeBucket === "oth99Duties") {
            if (oth99RemainingForExtras <= 0.5) continue;
            surfaceAmt = Math.min(amt, oth99RemainingForExtras);
            oth99RemainingForExtras -= surfaceAmt;
          }
          const d = detailMap.get(code);
          pairs.push({
            ace: {
              label: `Ch99 ${formatCh99Dots(code)}`,
              code,
              description: d?.description,
              rate: gv > 0 ? surfaceAmt / gv : 0,
              amount: surfaceAmt,
            },
            expected: null,
            status: "extra",
          });
        }
      }
    }

    pairs.sort((a, b) => {
      const ord = (s: Pair["status"]) => (s === "match" ? 1 : 0);
      const so = ord(a.status) - ord(b.status);
      if (so !== 0) return so;
      const gap = (p: Pair) => Math.abs((p.ace?.amount || 0) - (p.expected?.amount || 0));
      return gap(b) - gap(a);
    });

    const STATUS_BG: Record<Pair["status"], string> = {
      match: "#FFFFFF",
      over: "#FEF2F2",
      under: "#FFFBEB",
      missing: "#FEF2F2",
      extra: "#FEF2F2",
    };
    const PILL_BG: Record<Pair["status"], string> = {
      match: "#DCFCE7",
      over: "#FEE2E2",
      under: "#FEF3C7",
      missing: "#FEE2E2",
      extra: "#FEE2E2",
    };
    const PILL_FG: Record<Pair["status"], string> = {
      match: "#166534",
      over: "#991B1B",
      under: "#92400E",
      missing: "#991B1B",
      extra: "#991B1B",
    };
    const fmtPct = (r: number) => `${(r * 100).toFixed(2)}%`;
    const fmtSignedDollars = (n: number) => `${n >= 0 ? "+" : "-"}${currency0(Math.abs(n))}`;
    const pillLabel = (p: Pair) => {
      if (p.status === "match") return "Match";
      if (p.status === "over") return `Overpaid ${fmtSignedDollars((p.ace?.amount || 0) - (p.expected?.amount || 0))}`;
      if (p.status === "under") return `Underpaid ${fmtSignedDollars((p.ace?.amount || 0) - (p.expected?.amount || 0))}`;
      if (p.status === "missing") return `Missing ${currency0(p.expected?.amount || 0)}`;
      return `Extra +${currency0(p.ace?.amount || 0)}`;
    };

    const totalDiff = (aceDuty || 0) - (correctDuty || 0);
    const totalStatus: Pair["status"] = Math.abs(totalDiff) < 1 ? "match" : totalDiff > 0 ? "over" : "under";
    const totalLabel =
      totalStatus === "match"
        ? "Match"
        : totalStatus === "over"
        ? `+${currency0(Math.abs(totalDiff))} overpaid`
        : `-${currency0(Math.abs(totalDiff))} underpaid`;

    const renderSide = (side: Side | null, kind: "ace" | "expected") => {
      if (!side) {
        return (
          <Box sx={{ border: `1px dashed ${tokens.border.default}`, borderRadius: 1, p: 1, textAlign: "center", color: tokens.text.muted, fontStyle: "italic", fontSize: tokens.fontSize.xs, my: 0.25 }}>
            {kind === "ace" ? "(not filed)" : "(not expected)"}
          </Box>
        );
      }
      const isMono = !!side.code;
      return (
        <Box sx={{ p: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: 0.25, minWidth: 0 }}>
          <Typography
            noWrap
            title={side.label}
            sx={{
              fontSize: isMono ? tokens.fontSize.xs : tokens.fontSize.sm2,
              fontWeight: tokens.fontWeight.semibold,
              fontFamily: isMono ? "monospace" : undefined,
              color: tokens.text.primary,
            }}
          >
            {side.label}
          </Typography>
          {side.description && (
            <Typography
              noWrap
              title={side.description}
              sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, lineHeight: 1.3 }}
            >
              {side.description}
            </Typography>
          )}
          <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ fontVariantNumeric: "tabular-nums" }}>
            <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted }}>{fmtPct(side.rate || 0)}</Typography>
            <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted }}>·</Typography>
            <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.semibold, color: tokens.text.primary }}>
              {currency0(side.amount || 0)}
            </Typography>
          </Stack>
        </Box>
      );
    };

    return (
      <Box sx={{ border: `1px solid ${tokens.border.default}`, borderRadius: tokens.radius.md, overflow: "hidden" }}>
        {/* Summary header */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", borderBottom: `1px solid ${tokens.border.default}`, bgcolor: tokens.bg.surface }}>
          <Box sx={{ p: 1.5, textAlign: "left" }}>
            <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: tokens.fontWeight.semibold }}>ACE Duty (filed)</Typography>
            <Typography sx={{ fontSize: tokens.fontSize.lg, fontWeight: tokens.fontWeight.bold, fontVariantNumeric: "tabular-nums", mt: 0.25 }}>{currency0(aceDuty)}</Typography>
          </Box>
          <Box sx={{ p: 1.5, textAlign: "left", borderLeft: `1px solid ${tokens.border.default}`, borderRight: `1px solid ${tokens.border.default}` }}>
            <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: tokens.fontWeight.semibold }}>Correct Duty (Riffs)</Typography>
            <Typography sx={{ fontSize: tokens.fontSize.lg, fontWeight: tokens.fontWeight.bold, fontVariantNumeric: "tabular-nums", mt: 0.25 }}>{currency0(correctDuty || 0)}</Typography>
          </Box>
          <Box sx={{ p: 1.5, textAlign: "left" }}>
            <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: tokens.fontWeight.semibold }}>Difference</Typography>
            <Typography
              sx={{
                fontSize: tokens.fontSize.lg,
                fontWeight: tokens.fontWeight.bold,
                fontVariantNumeric: "tabular-nums",
                mt: 0.25,
                color: totalDiff > 0 ? tokens.status.success : totalDiff < 0 ? tokens.status.danger : tokens.text.primary,
              }}
            >
              {totalDiff === 0 ? "$0" : `${totalDiff > 0 ? "+" : "-"}${currency0(Math.abs(totalDiff))}`}
            </Typography>
          </Box>
        </Box>

        {/* When ACE breakdown is missing, just show the helper note */}
        {!ace ? (
          <Box sx={{ p: 2, color: tokens.text.muted, fontSize: tokens.fontSize.sm }}>
            Per-authority ACE breakdown not available for this finding — only totals shown above.
          </Box>
        ) : (
          <>
            {/* Subheader */}
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 168px 1fr", py: 0.75, px: 1.5, bgcolor: tokens.bg.muted, borderBottom: `1px solid ${tokens.border.default}` }}>
              <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: tokens.fontWeight.semibold }}>Filed in ACE</Typography>
              <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.disabled, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: tokens.fontWeight.bold, textAlign: "center" }}>Compare</Typography>
              <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: tokens.fontWeight.semibold }}>Expected (Riffs analysis)</Typography>
            </Box>

            {/* Pair rows */}
            {pairs.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center", color: tokens.text.muted, fontSize: tokens.fontSize.sm }}>No breakdown data.</Box>
            ) : (
              pairs.map((p, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 168px 1fr",
                    alignItems: "stretch",
                    bgcolor: STATUS_BG[p.status],
                    borderBottom: `1px solid ${tokens.border.default}`,
                  }}
                >
                  {renderSide(p.ace, "ace")}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", borderLeft: `1px solid ${tokens.border.default}`, borderRight: `1px solid ${tokens.border.default}`, px: 1 }}>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        px: 1,
                        py: 0.25,
                        borderRadius: 0.75,
                        fontSize: 11,
                        fontWeight: 600,
                        bgcolor: PILL_BG[p.status],
                        color: PILL_FG[p.status],
                        whiteSpace: "nowrap",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {pillLabel(p)}
                    </Box>
                  </Box>
                  {renderSide(p.expected, "expected")}
                </Box>
              ))
            )}

            {/* Footer total */}
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 168px 1fr", alignItems: "center", py: 1.25, px: 1.5, bgcolor: tokens.bg.muted, borderTop: `1px solid ${tokens.border.default}` }}>
              <Typography sx={{ fontSize: tokens.fontSize.base, fontWeight: tokens.fontWeight.bold, fontVariantNumeric: "tabular-nums" }}>{currency0(aceDuty || 0)}</Typography>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    px: 1,
                    py: 0.25,
                    borderRadius: 0.75,
                    fontSize: 11,
                    fontWeight: 600,
                    bgcolor: PILL_BG[totalStatus],
                    color: PILL_FG[totalStatus],
                    whiteSpace: "nowrap",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {totalLabel}
                </Box>
              </Box>
              <Typography sx={{ fontSize: tokens.fontSize.base, fontWeight: tokens.fontWeight.bold, fontVariantNumeric: "tabular-nums" }}>{currency0(correctDuty || 0)}</Typography>
            </Box>
          </>
        )}
      </Box>
    );
  };

  // Legacy — keep stub to not break call sites until the body below is removed

  return (
    <>
      <Stack sx={{ height: "100%", minHeight: 0, flex: 1, flexDirection: "column" }}>
        {/* Toolbar — matches verification-tab style */}
        <Stack direction="row" alignItems="center" sx={{ flexShrink: 0, px: 3, pt: 2, pb: 1.5, gap: 1.5 }}>
          <DateRangeFilter
            value={dateFilter}
            onSelectDate={(range: { startDate?: Date; endDate?: Date }) => {
              setDateFilter(range);
              setPage(INITIAL_PAGE);
            }}
          />
          <TariffAnalysisFilters
            manufacturers={manufacturerIds}
            originCodes={originCodes}
            htsCodes={htsCodes}
            signs={signs}
            authorities={authorityFilters}
            hasCh99={hasCh99}
            metalSplitOnly={metalSplitOnly}
            minAbsDifference={minAbsDifference}
            maxAbsDifference={maxAbsDifference}
            authorityAmounts={authorityAmounts}
            onApply={({
              manufacturers,
              originCodes: oc,
              htsCodes: hc,
              signs: nextSigns,
              authorities,
              hasCh99: nextHasCh99,
              metalSplitOnly: nextMetalSplitOnly,
              minAbsDifference: nextMinAbsDifference,
              maxAbsDifference: nextMaxAbsDifference,
            }) => {
              setManufacturerIds(manufacturers);
              setOriginCodes(oc);
              setHtsCodes(hc);
              setSigns(nextSigns);
              setAuthorityFilters(authorities);
              setHasCh99(nextHasCh99);
              setMetalSplitOnly(nextMetalSplitOnly);
              setMinAbsDifference(nextMinAbsDifference);
              setMaxAbsDifference(nextMaxAbsDifference);
              setPage(INITIAL_PAGE);
            }}
            manufacturerOptions={manufacturerOptions as any[]}
            countryOptions={countryOptions as any[]}
            htsOptions={htsOnlyOptions}
          />
          <ToggleButtonGroup
            exclusive
            size="small"
            value={viewMode}
            onChange={(_, nextView: TariffAnalysisViewMode | null) => {
              if (!nextView) return;
              setViewMode(nextView);
              setPage(INITIAL_PAGE);
            }}
            sx={{
              bgcolor: tokens.bg.surface,
              borderRadius: tokens.radius.md,
              "& .MuiToggleButton-root": {
                px: 1.5,
                py: 0.6,
                color: tokens.text.body,
                borderColor: tokens.border.default,
                fontSize: tokens.fontSize.sm2,
                fontWeight: tokens.fontWeight.semibold,
                textTransform: "none",
                "&.Mui-selected": {
                  bgcolor: tokens.bg.muted,
                  color: tokens.brand.primary,
                },
                "&.Mui-selected:hover": {
                  bgcolor: tokens.bg.hover,
                },
              },
            }}
          >
            <ToggleButton value="flat">Flat</ToggleButton>
            <ToggleButton value="aiAnalysis">AI Analysis</ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ ml: "auto" }} />

          <TextField
            size="small"
            placeholder="Search entry summaries..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(INITIAL_PAGE);
            }}
            sx={{
              width: 240,
              "& .MuiOutlinedInput-root": {
                borderRadius: tokens.radius.md,
                fontSize: tokens.fontSize.sm2,
                bgcolor: tokens.bg.surface,
              },
            }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleRebuild}
            disabled={isStartingBuild || isBuildInProgress || shouldSkip}
            startIcon={
              isStartingBuild || isBuildInProgress ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <AIIcon sx={{ fontSize: 16 }} />
              )
            }
            sx={{
              fontWeight: tokens.fontWeight.bold,
              fontSize: tokens.fontSize.md,
              borderRadius: tokens.radius.md,
              px: 2.5,
              py: 0.75,
              bgcolor: tokens.brand.primary,
              "&:hover": { bgcolor: tokens.brand.primaryHover },
            }}
          >
            {isBuildInProgress
              ? `Analyzing... ${Math.round(buildStatus?.progress || 0)}%`
              : isStartingBuild
              ? "Starting..."
              : "Build Analysis"}
          </Button>
          {isAiView && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleRunAiAnalysis}
              disabled={isTriggeringBulk || shouldSkip || anyEnrichmentRunning}
              startIcon={
                isTriggeringBulk || anyEnrichmentRunning ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <AIIcon sx={{ fontSize: 16 }} />
                )
              }
              sx={{
                fontWeight: tokens.fontWeight.bold,
                fontSize: tokens.fontSize.md,
                borderRadius: tokens.radius.md,
                px: 2.5,
                py: 0.75,
                color: tokens.brand.primary,
                borderColor: tokens.brand.primary,
                "&:hover": {
                  borderColor: tokens.brand.primaryHover,
                  bgcolor: `${tokens.brand.primary}10`,
                },
              }}
            >
              {anyEnrichmentRunning
                ? "AI agents running…"
                : isTriggeringBulk
                ? "Queuing…"
                : "Run AI Analysis"}
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            onClick={handleClear}
            sx={{
              fontWeight: tokens.fontWeight.bold,
              fontSize: tokens.fontSize.md,
              borderRadius: tokens.radius.md,
              px: 2,
              py: 0.75,
              color: tokens.text.body,
              borderColor: tokens.border.default,
              "&:hover": { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover },
            }}
          >
            Clear
          </Button>
        </Stack>

        {/* Build progress bar */}
        {isBuildInProgress && (
          <Box sx={{ px: 3, pb: 1 }}>
            <LinearProgress
              variant="determinate"
              value={Math.round(buildStatus?.progress || 0)}
              sx={{
                height: 4,
                borderRadius: 2,
                bgcolor: tokens.bg.border,
                "& .MuiLinearProgress-bar": { bgcolor: tokens.brand.primary, borderRadius: 2 },
              }}
            />
          </Box>
        )}

        {/* Authority filter chips — every authority with $-amount + finding
            count visible at a glance. Click toggles that authority in the
            authorities filter. Hidden when no client-level summary loaded. */}
        {!shouldSkip && authoritySummaries.length > 0 && (
          <Box sx={{ px: 3, pb: 1.5 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ rowGap: 1 }}>
              {AUTHORITY_BUCKETS.map((auth) => {
                const meta = authorityAmounts.get(auth);
                if (!meta || meta.count === 0) return null;
                const active = authorityFilters.includes(auth);
                const fmtDollars = (n: number) =>
                  Math.abs(n) >= 1_000_000
                    ? `$${(n / 1_000_000).toFixed(1)}M`
                    : Math.abs(n) >= 1_000
                    ? `$${(n / 1_000).toFixed(1)}K`
                    : `$${Math.round(n)}`;
                return (
                  <Chip
                    key={auth}
                    label={`${auth} · ${fmtDollars(meta.dollars)} · ${meta.count.toLocaleString()} findings`}
                    size="small"
                    onClick={() => {
                      setAuthorityFilters((prev) =>
                        prev.includes(auth) ? prev.filter((x) => x !== auth) : [...prev, auth]
                      );
                      setPage(INITIAL_PAGE);
                    }}
                    sx={{
                      height: 26,
                      fontSize: tokens.fontSize.xs,
                      fontWeight: tokens.fontWeight.semibold,
                      borderRadius: tokens.radius.md,
                      cursor: "pointer",
                      bgcolor: active ? tokens.brand.primary : `${tokens.brand.primary}10`,
                      color: active ? "#FFFFFF" : tokens.brand.primary,
                      border: `1px solid ${active ? tokens.brand.primary : `${tokens.brand.primary}30`}`,
                      "&:hover": {
                        bgcolor: active ? tokens.brand.primaryHover : `${tokens.brand.primary}20`,
                      },
                    }}
                  />
                );
              })}
            </Stack>
          </Box>
        )}

        {/* Table section — card wraps table + pagination as one piece */}
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Stack sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            mx: 3,
            mb: 2,
            ...tableContainerSx,
            ...tableCompactSx,
          }}>
            {isPageLoading ? (
              <Skeleton variant="rectangular" sx={{ flex: 1, borderRadius: "12px" }} />
            ) : (
              <>
                {viewMode === "flat" ? (
                  <>
                <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto", position: "relative" }}>
                  {isPageFetching && !isPageLoading && (
                    <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>
                      <CircularProgress size={20} />
                    </Box>
                  )}
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell align="left">Entry / Product</TableCell>
                        <TableCell align="left">Authorities</TableCell>
                        <TableCell align="center">Origin</TableCell>
                        <TableCell align="right">ACE Duty</TableCell>
                        <TableCell align="right">Correct Duty</TableCell>
                        <TableCell align="right">Difference</TableCell>
                        <TableCell align="left">Finding</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.length === 0 && !isPageFetching ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                            <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
                              No tariff analysis findings found.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        rows.map((item, idx) => {
                          const isBundle = !!item.isMetalSplitBundle;
                          const entryLine = isBundle && item.bundledLineNumbers?.length
                            ? `${item.entrySummaryNumber || "-"}-${item.bundledLineNumbers.join(", ")}`
                            : `${item.entrySummaryNumber || "-"}-${item.entrySummaryLineNumber ?? "-"}`;
                          const hts = formatHTS(item.htsNumberFull);
                          const desc = item.htsDescription ? ` - ${item.htsDescription}` : "";
                          const diff = Number(item.difference || 0);
                          const sign = diff > 0 ? "+" : diff < 0 ? "-" : "";
                          const diffColor = diff > 0 ? tokens.status.success : diff < 0 ? tokens.status.danger : tokens.text.primary;
                          const origin = item.countryOfOriginCode || "-";
                          const flag = origin !== "-" ? getCountryFlag(origin) : "";

                          return (
                            <TableRow
                              key={`${item.entrySummaryNumber}-${item.entrySummaryLineNumber}-${idx}`}
                              onClick={() => setSelectedRow(item)}
                              sx={{ cursor: "pointer" }}
                            >
                              <TableCell align="left">
                                <Stack spacing={0.25}>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography sx={{ fontWeight: tokens.fontWeight.bold, fontFamily: "monospace", fontSize: tokens.fontSize.base }}>{entryLine}</Typography>
                                    {isBundle && (
                                      <Chip label="Metal Split" size="small" sx={{ height: 18, fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.bold, bgcolor: "#EDE9FE", color: "#6D28D9", borderRadius: 0.5 }} />
                                    )}
                                  </Stack>
                                  {isBundle ? (
                                    <>
                                      {item.metalLines?.map((ml) => (
                                        <Typography key={`m-${ml.entrySummaryLineNumber}`} noWrap sx={{ color: tokens.text.secondary, fontSize: tokens.fontSize.xs, maxWidth: 280 }}>
                                          {formatHTS(ml.htsNumberFull)} ({ml.metalType === 'STEEL' ? 'Steel' : ml.metalType === 'ALUMINUM' ? 'Aluminum' : ml.metalType === 'COPPER' ? 'Copper' : 'Metal'})
                                        </Typography>
                                      ))}
                                      {item.nonMetalLines?.map((nml) => (
                                        <Typography key={`nm-${nml.entrySummaryLineNumber}`} noWrap sx={{ color: tokens.text.secondary, fontSize: tokens.fontSize.xs, maxWidth: 280 }}>
                                          {formatHTS(nml.htsNumberFull)} (Non-Metal)
                                        </Typography>
                                      ))}
                                    </>
                                  ) : (
                                    <Typography noWrap sx={{ color: tokens.text.secondary, fontSize: tokens.fontSize.xs, maxWidth: 280 }}>
                                      {hts}{desc}
                                    </Typography>
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell align="left">
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ rowGap: 0.5, maxWidth: 220 }}>
                                  {(item.discrepancyAuthorities || []).slice(0, 6).map((auth: string) => (
                                    <Chip
                                      key={auth}
                                      label={auth}
                                      size="small"
                                      sx={{
                                        height: 18,
                                        fontSize: tokens.fontSize["2xs"],
                                        fontWeight: tokens.fontWeight.bold,
                                        borderRadius: 0.5,
                                        bgcolor: tokens.bg.muted,
                                        color: tokens.text.secondary,
                                        border: `1px solid ${tokens.border.default}`,
                                      }}
                                    />
                                  ))}
                                  {(!item.discrepancyAuthorities || item.discrepancyAuthorities.length === 0) && (
                                    <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted }}>—</Typography>
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell align="center">
                                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                                  {flag && <span style={{ fontSize: 14 }}>{flag}</span>}
                                  <Typography sx={{ whiteSpace: "nowrap", fontWeight: tokens.fontWeight.semibold }}>{origin}</Typography>
                                </Stack>
                              </TableCell>
                              <TableCell align="right">
                                <Typography sx={{ whiteSpace: "nowrap", fontWeight: tokens.fontWeight.semibold }}>{currency0(item.aceDuty)}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography sx={{ whiteSpace: "nowrap", fontWeight: tokens.fontWeight.semibold }}>{currency0(item.correctDuty)}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography sx={{ whiteSpace: "nowrap", fontWeight: tokens.fontWeight.bold, color: diffColor }}>
                                  {sign}{currency0(Math.abs(diff))}
                                </Typography>
                              </TableCell>
                              <TableCell align="left">
                                <Tooltip title={item.finding || "-"} arrow placement="top-start">
                                  <Typography noWrap sx={{ maxWidth: 200, fontWeight: tokens.fontWeight.semibold }}>{item.finding || "-"}</Typography>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination — inside the card */}
                {totalCount > 0 && (
                  <Pagination
                    page={page}
                    limit={limit}
                    total={totalCount}
                    onPageChange={setPage}
                    onLimitChange={(l) => { setLimit(l); setPage(INITIAL_PAGE); }}
                  />
                )}
                </>
                ) : viewMode === "aiAnalysis" ? (
                  <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", position: "relative" }}>
                    {isPageFetching && !isPageLoading && (
                      <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>
                        <CircularProgress size={20} />
                      </Box>
                    )}
                    <AiBucketsList
                      buckets={aiBuckets}
                      totalDollars={aiClientTotal}
                      totalFindings={aiFindingsCount}
                      refundFindingsCount={aiRefundCount}
                      complianceFindingsCount={aiComplianceCount}
                      onRowClick={setSelectedRow}
                      enrichmentByBucketKey={enrichmentByBucketKey}
                      onOpenDrawer={(key: string) => setDrawerBucketKey(key)}
                      onRerun={(key: string) => {
                        const cid = (queryInput.input as any).clientId;
                        const tid = (queryInput.input as any).tenantId;
                        if (!cid) return;
                        triggerEnrichment({
                          input: { clientId: cid, tenantId: tid, bucketKey: key, force: true },
                        });
                      }}
                    />
                  </Box>
                ) : null}
              </>
            )}
          </Stack>
        </Box>
      </Stack>

      {/* Detail Drawer — redesigned to match discrepancy-detail-dialog style */}
      <Drawer anchor="right" open={!!selectedRow} onClose={() => setSelectedRow(null)}>
        {selectedRow && (
          <Box sx={{ width: { xs: "100vw", md: "min(1080px, 96vw)" }, maxWidth: "100vw", height: "100%", display: "flex", flexDirection: "column", bgcolor: tokens.bg.surface }}>

            {/* Header */}
            <Box sx={{ px: 3, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: tokens.bg.hover, borderBottom: `1px solid ${tokens.border.default}` }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary }}>
                  Tariff Analysis
                </Typography>
                {(() => {
                  const fc = getFindingColor(selectedRow.finding);
                  return (
                    <Chip
                      label={selectedRow.finding?.toUpperCase() || "REVIEW"}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: tokens.fontSize["2xs"],
                        fontWeight: tokens.fontWeight.bold,
                        borderRadius: 0.5,
                        bgcolor: fc.bg,
                        color: fc.color,
                        border: `1px solid ${fc.border}`,
                      }}
                    />
                  );
                })()}
              </Stack>
              <IconButton onClick={() => setSelectedRow(null)} size="small" sx={{ color: tokens.text.muted }}>
                <Close sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            {/* Scrollable content */}
            <Box sx={{ flex: 1, overflow: "auto" }}>

              {/* Entry identifier */}
              <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ fontSize: tokens.fontSize.lg, fontWeight: tokens.fontWeight.heavy, color: tokens.text.primary }}>
                    {selectedRow.isMetalSplitBundle && selectedRow.bundledLineNumbers?.length
                      ? `${selectedRow.entrySummaryNumber}-${selectedRow.bundledLineNumbers.join(", ")}`
                      : `${selectedRow.entrySummaryNumber}-${selectedRow.entrySummaryLineNumber ?? "-"}`}
                  </Typography>
                  {selectedRow.isMetalSplitBundle && (
                    <Chip label="Metal Split" size="small" sx={{ height: 20, fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.bold, bgcolor: "#EDE9FE", color: "#6D28D9", borderRadius: 0.5 }} />
                  )}
                </Stack>
                <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.secondary, mt: 0.25 }}>
                  {formatHTS(selectedRow.htsNumberFull)}{selectedRow.htsDescription ? ` - ${selectedRow.htsDescription}` : ""}
                </Typography>
              </Box>

              {/* Duty comparison cards */}
              <Box sx={{ px: 3, pb: 2.5 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ flex: 1, border: `1px solid ${tokens.border.default}`, borderRadius: tokens.radius.lg, p: 2, textAlign: "center" }}>
                    <Typography sx={hdr}>ACE Duty</Typography>
                    <Typography sx={{ fontSize: "1.5rem", fontWeight: tokens.fontWeight.heavy, color: tokens.text.primary, mt: 0.5 }}>
                      {currency0(selectedRow.aceDuty)}
                    </Typography>
                    <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, mt: 0.25 }}>
                      {selectedRow.isMetalSplitBundle ? "Combined paid" : "What was paid"}
                    </Typography>
                  </Box>
                  <ArrowIcon sx={{ color: tokens.text.disabled, fontSize: 20, flexShrink: 0 }} />
                  <Box sx={{ flex: 1, border: `1px solid ${tokens.border.default}`, borderRadius: tokens.radius.lg, p: 2, textAlign: "center" }}>
                    <Typography sx={hdr}>Correct Duty</Typography>
                    <Typography sx={{ fontSize: "1.5rem", fontWeight: tokens.fontWeight.heavy, color: tokens.text.primary, mt: 0.5 }}>
                      {currency0(selectedRow.correctDuty)}
                    </Typography>
                    <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, mt: 0.25 }}>
                      {selectedRow.isMetalSplitBundle ? "Combined expected" : "Riffs calculation"}
                    </Typography>
                  </Box>
                </Stack>

                {/* Difference + Goods Value */}
                <Stack direction="row" spacing={3} sx={{ mt: 2, justifyContent: "center" }}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography sx={hdr}>Difference</Typography>
                    <Typography sx={{
                      fontSize: "1.1rem",
                      fontWeight: tokens.fontWeight.heavy,
                      mt: 0.25,
                      color: Number(selectedRow.difference || 0) > 0 ? tokens.status.success : Number(selectedRow.difference || 0) < 0 ? tokens.status.danger : tokens.text.primary,
                    }}>
                      {Number(selectedRow.difference || 0) > 0 ? "+" : ""}{currency0(selectedRow.difference)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography sx={hdr}>Goods Value</Typography>
                    <Typography sx={{ fontSize: "1.1rem", fontWeight: tokens.fontWeight.heavy, color: tokens.text.primary, mt: 0.25 }}>
                      {currency0(selectedRow.goodsValue)}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              {/* ── Metal Split: dedicated per-line cards ── */}
              {selectedRow.isMetalSplitBundle && (selectedRow.metalLines?.length || selectedRow.nonMetalLines?.length) ? (
                <>
                  {/* Per-line cards */}
                  {[...(selectedRow.metalLines || []).map((l) => ({ ...l, _isMetal: true as const })),
                    ...(selectedRow.nonMetalLines || []).map((l) => ({ ...l, _isMetal: false as const }))]
                    .sort((a, b) => a.entrySummaryLineNumber - b.entrySummaryLineNumber)
                    .map((line) => {
                      const lineDiff = line.aceDuty - line.correctDuty;
                      const borderColor = line._isMetal ? "#7C3AED" : tokens.border.default;

                      return (
                        <Box key={`${line._isMetal ? "m" : "nm"}-${line.entrySummaryLineNumber}`} sx={{ px: 3, pb: 2 }}>
                          <Box sx={{ border: `1px solid ${borderColor}`, borderRadius: tokens.radius.lg, overflow: "hidden" }}>
                            {/* Line header */}
                            <Box sx={{
                              px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between",
                              bgcolor: line._isMetal ? "#EDE9FE" : tokens.bg.hover,
                              borderBottom: `1px solid ${borderColor}`,
                            }}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography sx={{ fontFamily: "monospace", fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.sm2, color: line._isMetal ? "#6D28D9" : tokens.text.body }}>
                                  Line {line.entrySummaryLineNumber}
                                </Typography>
                                <Chip
                                  label={
                                    line._isMetal
                                      ? (line as any).metalType === 'STEEL' ? 'Steel (232)'
                                        : (line as any).metalType === 'ALUMINUM' ? 'Aluminum (232)'
                                        : (line as any).metalType === 'COPPER' ? 'Copper (232)'
                                        : 'Metal'
                                      : "Non-Metal"
                                  }
                                  size="small"
                                  sx={{
                                    height: 18, fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.bold, borderRadius: 0.5,
                                    bgcolor: line._isMetal
                                      ? (line as any).metalType === 'STEEL' ? '#FEF3C7'
                                        : (line as any).metalType === 'ALUMINUM' ? '#E0E7FF'
                                        : (line as any).metalType === 'COPPER' ? '#FFE4E6'
                                        : '#DDD6FE'
                                      : "#F3F4F6",
                                    color: line._isMetal
                                      ? (line as any).metalType === 'STEEL' ? '#92400E'
                                        : (line as any).metalType === 'ALUMINUM' ? '#3730A3'
                                        : (line as any).metalType === 'COPPER' ? '#9F1239'
                                        : '#5B21B6'
                                      : tokens.text.secondary,
                                  }}
                                />
                              </Stack>
                              <Typography sx={{
                                fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.bold,
                                color: lineDiff > 0.01 ? tokens.status.success : lineDiff < -0.01 ? tokens.status.danger : tokens.text.muted,
                              }}>
                                {lineDiff > 0.01 ? "+" : ""}{currency0(lineDiff)}
                              </Typography>
                            </Box>

                            <Box sx={{ p: 2 }}>
                              {/* HTS */}
                              <Typography noWrap sx={{ color: tokens.text.secondary, fontSize: tokens.fontSize.xs, mb: 1.5 }}>
                                {formatHTS(line.htsNumberFull)}{line.htsDescription ? ` - ${line.htsDescription}` : ""}
                              </Typography>

                              {/* Summary row */}
                              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 1, mb: 1.5 }}>
                                <Box>
                                  <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, textTransform: "uppercase", letterSpacing: 0.3 }}>Goods Value</Typography>
                                  <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold }}>{currency0(line.goodsValue)}</Typography>
                                </Box>
                                <Box>
                                  <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, textTransform: "uppercase", letterSpacing: 0.3 }}>ACE Duty</Typography>
                                  <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold }}>{currency0(line.aceDuty)}</Typography>
                                </Box>
                                <Box>
                                  <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, textTransform: "uppercase", letterSpacing: 0.3 }}>Correct Duty</Typography>
                                  <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold }}>{currency0(line.correctDuty)}</Typography>
                                </Box>
                              </Box>

                              {/* Section 232 warning for non-metal line */}
                              {!line._isMetal && line.aceDutyBreakdown && line.aceDutyBreakdown.duty232 > 0 && (
                                <Box sx={{ bgcolor: "#FEF2F2", borderRadius: 1, px: 1.5, py: 0.75, mb: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.semibold, color: "#991B1B" }}>Section 232 on non-metal (unexpected)</Typography>
                                  <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.bold, color: "#991B1B" }}>{currency0(line.aceDutyBreakdown.duty232)}</Typography>
                                </Box>
                              )}

                              {/* Duty breakdown — Expected vs ACE per authority */}
                              {line.expectedBreakdown && line.expectedBreakdown.length > 0 && renderDutyBreakdownTable({
                                expectedBreakdown: line.expectedBreakdown,
                                ace: line.aceDutyBreakdown,
                                goodsValue: line.goodsValue,
                                aceDuty: line.aceDuty,
                                correctDuty: line.correctDuty,
                                expectedRate: line.expectedRate ?? 0,
                                lineCh99Codes: line.ch99CodeList,
                                dutyDict: (line as any).ch99DutyDict,
                                ch99Details: (line as any).ch99Details,
                              })}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}

                  {/* Entry metadata for bundles */}
                  <Box sx={{ px: 3, py: 2 }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2, border: `1px solid ${tokens.border.default}`, borderRadius: tokens.radius.lg, p: 2 }}>
                      <Box>
                        <Typography sx={hdr}>Entry Date</Typography>
                        <Typography sx={{ ...val, mt: 0.25 }}>
                          {(() => {
                            if (!selectedRow.entryDate) return "-";
                            const date = typeof selectedRow.entryDate === "string" ? parseISO(selectedRow.entryDate) : new Date(selectedRow.entryDate as any);
                            return isValid(date) ? format(date, "yyyy-MM-dd") : "-";
                          })()}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={hdr}>Origin</Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                          {selectedRow.countryOfOriginCode && (
                            <span style={{ fontSize: 16 }}>{getCountryFlag(selectedRow.countryOfOriginCode)}</span>
                          )}
                          <Typography sx={val}>{selectedRow.countryOfOriginCode || "-"}</Typography>
                        </Stack>
                      </Box>
                      <Box>
                        <Typography sx={hdr}>Manufacturer</Typography>
                        <Typography sx={{ ...val, mt: 0.25 }}>
                          {selectedRow.manufacturerID || "-"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </>
              ) : (
                <>

              {/* Single card with duty breakdown — matches metal split per-line card layout */}
              <Box sx={{ px: 3, pb: 2 }}>
                <Box sx={{ border: `1px solid ${tokens.border.default}`, borderRadius: tokens.radius.lg, overflow: "hidden" }}>
                  {/* Card header */}
                  <Box sx={{
                    px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between",
                    bgcolor: tokens.bg.hover,
                    borderBottom: `1px solid ${tokens.border.default}`,
                  }}>
                    <Typography sx={{ fontFamily: "monospace", fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.sm2, color: tokens.text.body }}>
                      Line {selectedRow.entrySummaryLineNumber ?? "-"}
                    </Typography>
                    {(() => {
                      const diff = selectedRow.aceDuty - selectedRow.correctDuty;
                      return (
                        <Typography sx={{
                          fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.bold,
                          color: diff > 0.01 ? tokens.status.success : diff < -0.01 ? tokens.status.danger : tokens.text.muted,
                        }}>
                          {diff > 0.01 ? "+" : ""}{currency0(diff)}
                        </Typography>
                      );
                    })()}
                  </Box>

                  <Box sx={{ p: 2 }}>
                    {/* HTS */}
                    <Typography noWrap sx={{ color: tokens.text.secondary, fontSize: tokens.fontSize.xs, mb: 1.5 }}>
                      {formatHTS(selectedRow.htsNumberFull)}{selectedRow.htsDescription ? ` - ${selectedRow.htsDescription}` : ""}
                    </Typography>

                    {/* Summary row */}
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 1, mb: 1.5 }}>
                      <Box>
                        <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, textTransform: "uppercase", letterSpacing: 0.3 }}>Goods Value</Typography>
                        <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold }}>{currency0(selectedRow.goodsValue)}</Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, textTransform: "uppercase", letterSpacing: 0.3 }}>ACE Duty</Typography>
                        <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold }}>{currency0(selectedRow.aceDuty)}</Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, textTransform: "uppercase", letterSpacing: 0.3 }}>Correct Duty</Typography>
                        <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold }}>{currency0(selectedRow.correctDuty)}</Typography>
                      </Box>
                    </Box>

                    {/* Duty breakdown table */}
                    {selectedRow.expectedBreakdown && selectedRow.expectedBreakdown.length > 0 && renderDutyBreakdownTable({
                      expectedBreakdown: selectedRow.expectedBreakdown,
                      ace: selectedRow.aceDutyBreakdown,
                      goodsValue: selectedRow.goodsValue || 0,
                      aceDuty: selectedRow.aceDuty,
                      correctDuty: selectedRow.correctDuty,
                      expectedRate: selectedRow.expectedRate ?? 0,
                      lineCh99Codes: selectedRow.ch99CodeList,
                      dutyDict: selectedRow.ch99DutyDict,
                      ch99Details: selectedRow.ch99Details,
                    })}
                  </Box>
                </Box>
              </Box>

              {/* Entry metadata */}
              <Box sx={{ px: 3, py: 2 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2, border: `1px solid ${tokens.border.default}`, borderRadius: tokens.radius.lg, p: 2 }}>
                  <Box>
                    <Typography sx={hdr}>Entry Date</Typography>
                    <Typography sx={{ ...val, mt: 0.25 }}>
                      {(() => {
                        if (!selectedRow.entryDate) return "-";
                        const date = typeof selectedRow.entryDate === "string" ? parseISO(selectedRow.entryDate) : new Date(selectedRow.entryDate as any);
                        return isValid(date) ? format(date, "yyyy-MM-dd") : "-";
                      })()}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={hdr}>Origin</Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                      {selectedRow.countryOfOriginCode && (
                        <span style={{ fontSize: 16 }}>{getCountryFlag(selectedRow.countryOfOriginCode)}</span>
                      )}
                      <Typography sx={val}>{selectedRow.countryOfOriginCode || "-"}</Typography>
                    </Stack>
                  </Box>
                  <Box>
                    <Typography sx={hdr}>HTS</Typography>
                    <Typography sx={{ ...val, fontFamily: "monospace", mt: 0.25 }}>
                      {formatHTS(selectedRow.htsNumberFull)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Recommendation */}
              {selectedRow.recommendation && (
                <Box sx={{ mx: 3, mb: 2, p: 2, bgcolor: tokens.status.warningBg, borderRadius: tokens.radius.lg, border: `1px solid ${tokens.status.warning}30` }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                    <RecommendIcon sx={{ fontSize: 16, color: tokens.status.warning }} />
                    <Typography sx={{ fontSize: tokens.fontSize.base, fontWeight: tokens.fontWeight.bold, color: tokens.status.warning }}>Recommendation</Typography>
                  </Stack>
                  <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.body, lineHeight: 1.6 }}>
                    {selectedRow.recommendation}
                  </Typography>
                </Box>
              )}
                </>
              )}
            </Box>

            {/* Footer */}
            <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${tokens.border.default}`, display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={() => setSelectedRow(null)}
                sx={{ fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.sm2, borderRadius: tokens.radius.md, px: 3, py: 0.75, color: tokens.text.body, borderColor: tokens.border.default, "&:hover": { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover } }}
              >
                Close
              </Button>
            </Box>
          </Box>
        )}
      </Drawer>

      <AiAnalysisDrawer
        enrichment={drawerBucketKey ? (enrichmentByBucketKey.get(drawerBucketKey) || null) : null}
        bucketName={(() => {
          const b = aiBuckets.find((x) => {
            const k = x.subKey ? `${x.patternId}|${x.direction}|${x.subKey}` : `${x.patternId}|${x.direction}|`;
            return k === drawerBucketKey;
          });
          return b?.name || "";
        })()}
        bucketAbsDollars={(() => {
          const b = aiBuckets.find((x) => {
            const k = x.subKey ? `${x.patternId}|${x.direction}|${x.subKey}` : `${x.patternId}|${x.direction}|`;
            return k === drawerBucketKey;
          });
          return b?.absDollars || 0;
        })()}
        onClose={() => setDrawerBucketKey(null)}
      />
    </>
  );
};

export default TariffAnalysisView;
